/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react"

// @mui material components
import Divider from "@mui/material/Divider"
import Switch from "@mui/material/Switch"
import Link from "@mui/material/Link"
import Icon from "@mui/material/Icon"
import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Tooltip from "@mui/material/Tooltip"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

// Custom styles for the Configurator
import ConfiguratorRoot from "monitor/Configurator/ConfiguratorRoot"
import SiteImagesSection from "monitor/Configurator/SiteImagesSection"

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import Swal from "sweetalert2"
import { showToast } from "utils/swalToast"
// Material Dashboard 2 React context
import { useMaterialUIController, setOpenConfigurator, setDarkMode } from "context"
import { setConfiguratorPage } from "context"
import { fetchDeviceConfigs, toggleOnlineStatus, openDoor } from "layouts/dispositivos/service"
import { jwtDecode } from "jwt-decode"

function DeviceRow({ device }) {
  const [isOnline, setIsOnline] = useState(Boolean(device.online))
  const [onlineLoading, setOnlineLoading] = useState(true)
  const [openingDoor, setOpeningDoor] = useState(false)

  useEffect(() => {
    let isMounted = true
    const check = async () => {
      try {
        const data = await toggleOnlineStatus(device.id)
        if (isMounted) setIsOnline(Boolean(data?.online))
      } catch {
        if (isMounted) setIsOnline(false)
      } finally {
        if (isMounted) setOnlineLoading(false)
      }
    }
    check()
    return () => {
      isMounted = false
    }
  }, [device.id])

  const handleOpenDoor = async () => {
    const result = await Swal.fire({
      title: "¿Abrir la puerta?",
      text: `Se enviará la señal de apertura al dispositivo ${device.description}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, abrir",
      cancelButtonText: "Cancelar",
    })
    if (result.isConfirmed) {
      try {
        setOpeningDoor(true)
        await openDoor(device.id)
        showToast({ title: "Apertura enviada con éxito", icon: "success" })
      } catch (error) {
        showToast({ icon: "error", title: "Error", text: error.message })
      } finally {
        setOpeningDoor(false)
      }
    }
  }

  const nombre = `${device.description} - ${device.sucursales?.empresa?.descripcion ?? ""}/${device.sucursales?.descripcion ?? "-"}`

  return (
    <MDBox
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      py={0.75}
      px={1}
      borderRadius="md"
      sx={{ "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" } }}
    >
      <MDBox display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
        {onlineLoading ? (
          <CircularProgress size={16} sx={{ flexShrink: 0 }} />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            color={isOnline ? "success" : "error"}
            label={isOnline ? "Online" : "Offline"}
            icon={<Icon fontSize="small">{isOnline ? "check_circle" : "cancel"}</Icon>}
            sx={{ flexShrink: 0 }}
          />
        )}
        <MDTypography
          variant="caption"
          color="text"
          sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {nombre}
        </MDTypography>
      </MDBox>

      {device.type === "PORTAL_ACCESO" && isOnline && !onlineLoading && (
        <Tooltip title="Enviar señal de apertura al portal de acceso">
          <span>
            <MDButton
              variant="text"
              color="info"
              size="small"
              disabled={openingDoor}
              onClick={handleOpenDoor}
              sx={{ minWidth: 0, px: 1, flexShrink: 0 }}
            >
              <Icon>lock_open</Icon>&nbsp;Abrir
            </MDButton>
          </span>
        </Tooltip>
      )}
    </MDBox>
  )
}

const getAuthToken = () => (sessionStorage.getItem("token") || localStorage.getItem("token")) || sessionStorage.getItem("token")

const normalizePerms = (source) => {
  if (!source) return []

  if (Array.isArray(source)) {
    return source.flatMap((item) => normalizePerms(item))
  }

  if (typeof source === "string") {
    const trimmed = source.trim()
    if (!trimmed) return []

    try {
      const parsed = JSON.parse(trimmed)
      if (parsed !== trimmed) {
        return normalizePerms(parsed)
      }
    } catch {
      // no-op: plain string permission
    }

    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
  }

  if (typeof source === "object") {
    const candidateKeys = ["perm", "permission", "name", "code", "authority", "descripcion"]
    for (const key of candidateKeys) {
      if (typeof source[key] === "string" && source[key].trim()) {
        return [source[key].trim()]
      }
    }

    return Object.values(source).flatMap((item) => normalizePerms(item))
  }

  return []
}

const hasBearerPermission = (permissionName) => {
  const token = getAuthToken()
  if (!token) return false

  try {
    const decoded = jwtDecode(token)
    const rawPerms = decoded?.perms ?? decoded?.permissions ?? decoded?.authorities
    const perms = normalizePerms(rawPerms)
    return perms.includes(permissionName)
  } catch {
    return false
  }
}


function Configurator() {
  const [controller, dispatch] = useMaterialUIController()
  const { openConfigurator, darkMode, configuratorPage } = controller
  const [isChecked, setIsChecked] = useState(() => {
    const saved = localStorage.getItem("checkboxState")
    if (saved) {
      return saved === "true" // localStorage guarda los valores como strings
    } else {
      return true
    }
  })
  const [devicesLoading, setDevicesLoading] = useState(false)
  const [devicesError, setDevicesError] = useState("")
  const [devicesRows, setDevicesRows] = useState([])

  const [page, setPage] = useState(0)
  const size = 5

  // opcional si tu backend retorna PageImpl (Spring)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)

  useEffect(() => {
    localStorage.setItem("checkboxState", isChecked)
    setDarkMode(dispatch, isChecked)
  }, [dispatch, isChecked])

  const loadDevices = async (pageToLoad = 0) => {
    try {
      setDevicesError("")
      setDevicesLoading(true)

      const result = await fetchDeviceConfigs(pageToLoad, size)

      // Caso tipico Spring Page: { content, totalPages, totalElements, number, ... }
      const content = Array.isArray(result?._embedded?.configDeviceList)
        ? result._embedded.configDeviceList
        : Array.isArray(result?.content)
          ? result.content
          : Array.isArray(result)
            ? result
            : []

      setDevicesRows(content)
      setTotalPages(Number(result?.page?.totalPages ?? result?.totalPages ?? 0))
      setTotalElements(Number(result?.page?.totalElements ?? result?.totalElements ?? content.length))
      setPage(Number(result?.page?.number ?? result?.number ?? pageToLoad))
    } catch (e) {
      setDevicesError(e?.message || "Error al cargar dispositivos")
      setDevicesRows([])
      setTotalPages(0)
      setTotalElements(0)
    } finally {
      setDevicesLoading(false)
    }
  }

  useEffect(() => {
    if (openConfigurator && configuratorPage === "devices") {
      loadDevices(0)
    }
  }, [openConfigurator, configuratorPage])


  const canEditSiteImages = hasBearerPermission("EMPRESA_EDITAR")

  const activeTab = configuratorPage === "devices" ? 1 : 0

  const handleTabChange = (_, newValue) => {
    const newPage = newValue === 1 ? "devices" : "default"
    setConfiguratorPage(dispatch, newPage)
    if (newValue === 1) loadDevices(0)
  }

  const handleCloseConfigurator = () => {
    setOpenConfigurator(dispatch, false)
    setConfiguratorPage(dispatch, "default")
  }

  const handleCheckboxChange = (event) => {
    setIsChecked(!event.target.checked)
    setDarkMode(dispatch, !isChecked)
  }

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      {/* Header compartido */}
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={4}
        pb={0.5}
        px={3}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="inherit"
          TabIndicatorProps={{ style: { backgroundColor: "#4CAF50" } }}
          sx={{
            "& .MuiTab-root": {
              color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)",
              fontWeight: 500,
              minWidth: 0,
              px: 2,
              mr: 1,
            },
            "& .Mui-selected": {
              color: darkMode ? "#fff !important" : "rgba(0,0,0,0.87) !important",
              fontWeight: 700,
            },
          }}
        >
          <Tab label="Configuración" />
          <Tab label="Dispositivos" />
        </Tabs>

        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </MDBox>

      <Divider />

      {/* Tab: Configuración */}
      {activeTab === 0 && (
        <MDBox pt={0.5} pb={3} px={3}>
          <MDTypography variant="body2" color="text">
            Opciones de accesibilidad.
          </MDTypography>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
            <MDTypography variant="h6">Oscuro / Claro</MDTypography>
            <Switch checked={!isChecked} onChange={handleCheckboxChange} />
          </MDBox>

          <Divider />

          {canEditSiteImages && (
            <>
              <SiteImagesSection
                openConfigurator={openConfigurator}
                companyName={sessionStorage.getItem("company") || localStorage.getItem("company")}
                companyId={sessionStorage.getItem("companyId") || localStorage.getItem("companyId")}
              />
              <Divider />
            </>
          )}

          <MDBox mt={4} mb={4} textAlign="center">
            <MDBox display="flex" justifyContent="center">
              <MDBox mr={1.5}>
                <MDButton
                  component={Link}
                  href="https://drive.google.com/file/d/1G6e-3DrhAh6FTmwWS-PTi-s7HRZME49_/view?usp=sharing"
                  target="_blank"
                  rel="noreferrer"
                  color="dark"
                >
                  <PictureAsPdfIcon />
                  &nbsp; Manual de usuario
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      )}

      {/* Tab: Dispositivos */}
      {activeTab === 1 && (
        <MDBox pt={2} pb={3} px={2}>
          {devicesLoading && (
            <MDBox display="flex" alignItems="center" gap={1} px={1}>
              <CircularProgress size={16} />
              <MDTypography variant="body2" color="text">
                Cargando dispositivos...
              </MDTypography>
            </MDBox>
          )}

          {!!devicesError && (
            <MDTypography variant="body2" color="error" px={1}>
              {devicesError}
            </MDTypography>
          )}

          {!devicesLoading && !devicesError && (
            <>
              <MDBox>
                {devicesRows.map((d) => (
                  <DeviceRow key={d.id} device={d} />
                ))}
              </MDBox>

              {totalElements > size && (
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2} px={1}>
                  <MDTypography variant="caption" color="text">
                    Total: {totalElements}
                  </MDTypography>

                  <MDBox display="flex" gap={1} alignItems="center">
                    <MDButton
                      variant="outlined"
                      color="info"
                      size="small"
                      disabled={page <= 0 || devicesLoading}
                      onClick={() => loadDevices(page - 1)}
                    >
                      Anterior
                    </MDButton>

                    <MDTypography variant="caption" color="text">
                      {page + 1} / {totalPages || 1}
                    </MDTypography>

                    <MDButton
                      variant="outlined"
                      color="info"
                      size="small"
                      disabled={
                        totalPages > 0
                          ? page >= totalPages - 1
                          : devicesRows.length < size || devicesLoading
                      }
                      onClick={() => loadDevices(page + 1)}
                    >
                      Siguiente
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}
            </>
          )}
        </MDBox>
      )}
    </ConfiguratorRoot>
  )
}

export default Configurator
